
import { CategoryModel, CategoryUserModel, TenantInvitationModel, TenantUserModel, LocationModel } from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import Page from '@/Page.vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import { tenantInvitationsStore, tenantUsersStore } from '@/libs/tenants/+state/store';

import AccountDialog from '@/libs/settings/components/AccountDialog.vue';
import InviteAccountDialog from '../components/InviteAccountDialog.vue';
import DeleteDialog from '@/libs/common/components/DeleteDialog.vue';
import ExportDialog from '@/libs/common/components-smart/ExportDialog.vue';
import { categoriesStore } from '@/libs/categories/+state/store';
import { locationsStore } from '@/libs/locations/+state/store';
import { departmentsStore } from '@/libs/departments/+state/store';
import { rolesStore } from '@/libs/roles/+state/store';
import { positionsStore } from '@/libs/positions/+state/store';
import ApiService from '@/libs/core/api-service';
import {
    AggregationEnum,
    QueryOperator,
    JunctionOperator,
    PlanItemStateEnum,
    ImplementationStateEnum,
    DepartmentModel,
    RoleModel,
    PositionModel,
} from '@/libs/Api';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import { AuthenticationType } from '@/libs/auth/models/auth-type';

@Component({
    components: {
        AccountDialog,
        InviteAccountDialog,
        DeleteDialog,
        ExportDialog,
    },
})
export default class Accounts extends Page {
    invitation: TenantInvitationModel | null = null;
    deleteInvitationDialog = false;
    deletingInvitation = false;
    tenantUser: TenantUserModel | null = null;
    deleting = false;
    canDeleteUser = true;
    activeAgendaCounts: Record<string, number> = {};
    filterIsActive: boolean | null = null;
    filterName = '';
    filterDepartmentId: string | null = null;
    filterLocationId: string | null = null;
    filterCategoryId: string | null = null;
    filterRoleId: string | null = null;
    filterEmployeeId = '';
    filterPositionId: string | null = null;
    mobileFiltersShow = false;
    nameDisplayFormat: 'firstLast' | 'lastFirst' = 'firstLast';
    itemsPerPage = 10;
    tab = 'accounts';

    @Prop() tenantId!: string;

    @Ref() accountDialog!: AccountDialog;
    @Ref() inviteAccountDialog!: InviteAccountDialog;
    @Ref() exportDialog!: ExportDialog;
    @Ref() deleteDialog!: DeleteDialog;

    @Watch('filterIsActive')
    filtersChanged() {
        this.loadItems();
    }

    @Watch('itemsPerPage')
    itemsPerPageChanged(v: number) {
        window.localStorage.amsItemsPerPage = v.toString();
    }

    get isSingleTenant(): boolean {
        return authStore.useGetter(AuthGetter.GetIsSingleTenant) ?? false;
    }

    get tableHeaders() {
        const isSingleTenant = authStore.useGetter(AuthGetter.GetIsSingleTenant) ?? false;
        return [
            { text: '', value: 'lastName', align: 'left', sortable: false }, // Dynamický název pro jméno
            { text: this.$i18n.t('settings.accounts.columnPhoneNumber'), value: 'phoneNumber', align: 'left', sortable: false },
            {
                text: this.$i18n.t(
                    isSingleTenant ? 'settings.accounts.columnEmailOrUserName' : 'settings.accounts.columnEmail'
                ),
                value: 'email',
                align: 'left',
                sortable: false,
            },
            // { text: this.$i18n.t("settings.accounts.allowedCategories"), value: "roles", align: "left", width: "40%", sortable: false },
            {
                text: this.$i18n.t('settings.accounts.columnRoles'),
                value: 'roles',
                align: 'left',
                width: '20%',
                sortable: false,
            },
            { text: this.$i18n.t('settings.accounts.department'), value: 'department.name', align: 'left', sortable: false },
            { text: this.$i18n.t('settings.accounts.employeeId'), value: 'employeeId', align: 'left', sortable: false },
            { text: this.$i18n.t('settings.accounts.position'), value: 'position.name', align: 'left', sortable: false },
            { text: this.$i18n.t('settings.accounts.allowedCategories'), value: 'categories', align: 'left', sortable: false },
            { text: this.$i18n.t('settings.accounts.allowedLocations'), value: 'locations', align: 'left', sortable: false },
            { text: '', value: 'isActive', align: 'left', width: '100px', sortable: false },
            { text: '', value: 'id', align: 'right', width: '120px', sortable: false },
        ];
    }

    get tableInvitationsHeaders() {
        return [
            { text: this.$i18n.t('settings.accounts.columnEmail'), value: 'emailAddress', align: 'left', sortable: false },
            {
                text: this.$i18n.t('settings.accounts.columnRoles'),
                value: 'roles',
                align: 'left',
                width: '40%',
                sortable: false,
            },
            { text: this.$i18n.t('settings.accounts.allowedLocations'), value: 'locations', align: 'left', sortable: false },
            { text: '', value: 'id', align: 'right', width: '120px', sortable: false },
        ];
    }

    get loading(): boolean {
        return tenantUsersStore.useGetter(CrudGetter.Pending);
    }

    get items(): TenantUserModel[] {
        let items = [...tenantUsersStore.useGetter(CrudGetter.Data)] as TenantUserModel[];

        // Filtrování jménem nebo příjmením
        if (this.filterName) {
            const searchTerm = this.filterName.toLowerCase();
            items = items.filter((item) => {
                const firstName = item.user?.firstName?.toLowerCase() || '';
                const lastName = item.user?.lastName?.toLowerCase() || '';
                return firstName.includes(searchTerm) || lastName.includes(searchTerm);
            });
        }

        // Filtrování osobním číslem
        if (this.filterEmployeeId) {
            const searchTerm = this.filterEmployeeId.toLowerCase();
            items = items.filter((item) => {
                const employeeId = item.employeeId?.toLowerCase() || '';
                return employeeId.includes(searchTerm);
            });
        }

        // Filtrování oddělením
        if (this.filterDepartmentId) {
            items = items.filter((item) => item.departmentId === this.filterDepartmentId);
        }

        // Filtrování pozicí
        if (this.filterPositionId) {
            items = items.filter((item) => item.positionId === this.filterPositionId);
        }

        // Filtrování lokací
        if (this.filterLocationId) {
            items = items.filter((item) => {
                return (item.locations || []).some((loc) => loc.locationId === this.filterLocationId);
            });
        }

        // Filtrování kategorií
        if (this.filterCategoryId) {
            items = items.filter((item) => {
                return (item.categories || []).some((cat) => cat.categoryId === this.filterCategoryId);
            });
        }

        // Filtrování rolí
        if (this.filterRoleId) {
            items = items.filter((item) => {
                return (item.userRoles || []).some((role) => role.roleId === this.filterRoleId);
            });
        }

        // Řazení podle formátu zobrazení jména
        return items
            .orderBy((x) => {
                if (this.nameDisplayFormat === 'lastFirst') {
                    const lastName = x.user?.lastName || '';
                    const firstName = x.user?.firstName || '';
                    return `${lastName} ${firstName}`.toLowerCase();
                } else {
                    const firstName = x.user?.firstName || '';
                    const lastName = x.user?.lastName || '';
                    return `${firstName} ${lastName}`.toLowerCase();
                }
            })
            .toArray();
    }

    get activeUserCount(): number {
        const items = [...tenantUsersStore.useGetter(CrudGetter.Data)] as TenantUserModel[];
        return items.count((x) => x.isActive == true);
    }

    get inactiveUserCount(): number {
        const items = [...tenantUsersStore.useGetter(CrudGetter.Data)] as TenantUserModel[];
        return items.count((x) => x.isActive != true);
    }

    get loadingInvitations(): boolean {
        return tenantInvitationsStore.useGetter(CrudGetter.Pending);
    }

    get invitations(): TenantInvitationModel[] {
        const invitations = [...tenantInvitationsStore.useGetter(CrudGetter.Data)] as TenantInvitationModel[];
        return invitations.orderBy((x) => x.emailAddress).toArray();
    }

    get categories(): CategoryModel[] {
        return [...categoriesStore.useGetter(CrudGetter.Data)];
    }

    get departments(): DepartmentModel[] {
        return [...departmentsStore.useGetter(CrudGetter.Data)];
    }

    get roles(): RoleModel[] {
        return [...rolesStore.useGetter(CrudGetter.Data)];
    }

    get positions(): PositionModel[] {
        return [...positionsStore.useGetter(CrudGetter.Data)];
    }

    get locations(): LocationModel[] {
        return [...locationsStore.useGetter(CrudGetter.Data)];
    }

    get enableInvitations(): boolean {
        //const authenticationType = authStore.useGetter(AuthGetter.GetAuthenticationType) as AuthenticationType;
        //return authenticationType != AuthenticationType.AzureActiveDirectory;
        return true;
    }

    mounted() {
        this.itemsPerPage = window.localStorage.amsItemsPerPage ? Number(window.localStorage.amsItemsPerPage) : 10;
        this.loadNameDisplayFormat();
        this.load();
    }

    load() {
        this.loadItems();
        this.loadInvitations();
        this.loadCategories();
        this.loadLocations();
        this.loadDepartments();
        this.loadRoles();
        this.loadPositions();
    }

    loadItems() {
        const filters = [];
        if (this.filterIsActive != null) {
            filters.push({ field: 'isActive', op: 'eq', comparand: this.filterIsActive });
        }
        tenantUsersStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<TenantUserModel>(undefined, filters));
    }

    loadInvitations() {
        tenantInvitationsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<TenantInvitationModel>(undefined, [{ field: 'isActive', op: 'eq', comparand: true }])
        );
    }

    loadCategories() {
        categoriesStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<CategoryModel>({ field: 'name', index: 1, order: 'asc' })
        );
    }

    loadLocations() {
        locationsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<LocationModel>({ field: 'name', index: 1, order: 'asc' })
        );
    }

    loadDepartments() {
        departmentsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<DepartmentModel>({ field: 'name', index: 1, order: 'asc' })
        );
    }

    loadRoles() {
        rolesStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<RoleModel>({ field: 'name', index: 1, order: 'asc' }));
    }

    loadPositions() {
        positionsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<PositionModel>({ field: 'name', index: 1, order: 'asc' })
        );
    }

    createItemClick() {
        this.inviteAccountDialog.open();
    }

    openItemClick(item: TenantUserModel) {
        this.accountDialog.open(item.id);
    }

    exportClick() {
        this.exportDialog.open();
    }

    deleteInvitationClick(invitation: TenantInvitationModel) {
        this.invitation = invitation;
        this.deleteInvitationDialog = true;
    }

    deleteInvitation() {
        tenantInvitationsStore.dispatch(
            CrudAction.Delete,
            createCrudQueryPayload<TenantInvitationModel>(undefined, [
                { field: 'id', op: 'eq', comparand: this.invitation!.id! },
            ])
        );
        this.deletingInvitation = true;
        this.subscribe(tenantInvitationsStore, CrudReponse.Delete)
            .then(() => {
                this.deleteInvitationDialog = false;
                this.deletingInvitation = false;
                this.loadInvitations();
            })
            .catch(() => {
                this.deletingInvitation = false;
            });
    }

    deleteItemClick(item: TenantUserModel) {
        this.tenantUser = item;
        this.checkUserAgendas(item.id!).then(() => {
            this.deleteDialog.open();
        });
    }

    deleteItem() {
        tenantUsersStore.dispatch(
            CrudAction.Delete,
            createCrudQueryPayload<TenantInvitationModel>(undefined, [
                { field: 'id', op: 'eq', comparand: this.tenantUser!.id! },
            ])
        );
        this.deleting = true;
        this.subscribe(tenantUsersStore, CrudReponse.Delete)
            .then(() => {
                this.deleteDialog.close();
                this.deleting = false;
                this.loadInvitations();
            })
            .catch(() => {
                this.deleting = false;
            });
    }

    agendaTypeText(type: string): string {
        const map: Record<string, string> = {
            Plan: String(this.$t('agendaTransfer.plan')),
            Findings: String(this.$t('agendaTransfer.findings')),
            Measures: String(this.$t('agendaTransfer.measures')),
            Verification: String(this.$t('agendaTransfer.verification')),
            Review: String(this.$t('agendaTransfer.review')),
        };
        return map[type] || type;
    }

    get filteredActiveAgendaEntries() {
        const order = ['Plan', 'Findings', 'Measures', 'Verification', 'Review'];
        const entries = Object.entries(this.activeAgendaCounts || {})
            .map(([type, count]) => ({ type, count: Number(count) }))
            .filter((e) => e.count > 0);
        return entries.sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));
    }

    async checkUserAgendas(userId: string) {
        this.canDeleteUser = true;
        this.activeAgendaCounts = {};

        try {
            const results = await Promise.all([
                // Plan
                (async () => {
                    const predicates = [
                        {
                            field: 'id',
                            op: QueryOperator.Wrap,
                            comparand: [
                                {
                                    field: 'tenantUserId',
                                    op: QueryOperator.Eq,
                                    comparand: userId,
                                    junction: JunctionOperator.Or,
                                },
                                {
                                    field: 'planItemAuditTenantUsers.tenantUserId',
                                    op: QueryOperator.Eq,
                                    comparand: userId,
                                    junction: JunctionOperator.Or,
                                },
                            ],
                        },
                        { field: 'isAnswered', op: QueryOperator.Eq, comparand: false },
                    ];
                    const aggregation = { predicates, aggregation: AggregationEnum.Count, aggregationProperty: 'id' } as any;
                    const response = await ApiService.api.aggregationPlanItem(aggregation);
                    let count = 0;
                    if (response && response.data && response.data.returnValue) {
                        const r = response.data.returnValue as any;
                        count = typeof r === 'number' ? r : typeof r === 'string' ? parseInt(r, 10) : 0;
                    }
                    return { type: 'Plan', count };
                })(),
                // Findings
                (async () => {
                    const predicates = [
                        { field: 'findingManagers.tenantUserId', op: QueryOperator.Eq, comparand: userId },
                        {
                            field: 'response.planItem.planItemState',
                            op: QueryOperator.Eq,
                            comparand: PlanItemStateEnum.NeedsToCreateMeasures,
                        },
                    ];
                    const aggregation = { predicates, aggregation: AggregationEnum.Count, aggregationProperty: 'id' } as any;
                    const response = await ApiService.api.aggregationFinding(aggregation);
                    let count = 0;
                    if (response && response.data && response.data.returnValue) {
                        const r = response.data.returnValue as any;
                        count = typeof r === 'number' ? r : typeof r === 'string' ? parseInt(r, 10) : 0;
                    }
                    return { type: 'Findings', count };
                })(),
                // Measures
                (async () => {
                    const predicates = [
                        {
                            field: 'id',
                            op: QueryOperator.Wrap,
                            comparand: [
                                {
                                    field: 'implementerUserId',
                                    op: QueryOperator.Eq,
                                    comparand: userId,
                                    junction: JunctionOperator.Or,
                                },
                                {
                                    field: 'measureImplementationUsers.tenantUserId',
                                    op: QueryOperator.Eq,
                                    comparand: userId,
                                    junction: JunctionOperator.Or,
                                },
                            ],
                        },
                        {
                            field: 'implementationState',
                            op: QueryOperator.In,
                            comparand: [ImplementationStateEnum.New, ImplementationStateEnum.InProgress],
                        },
                    ];
                    const aggregation = { predicates, aggregation: AggregationEnum.Count, aggregationProperty: 'id' } as any;
                    const response = await ApiService.api.aggregationMeasure(aggregation);
                    let count = 0;
                    if (response && response.data && response.data.returnValue) {
                        const r = response.data.returnValue as any;
                        count = typeof r === 'number' ? r : typeof r === 'string' ? parseInt(r, 10) : 0;
                    }
                    return { type: 'Measures', count };
                })(),
                // Verification
                (async () => {
                    const predicates = [
                        {
                            field: 'id',
                            op: QueryOperator.Wrap,
                            comparand: [
                                {
                                    field: 'verificationUserId',
                                    op: QueryOperator.Eq,
                                    comparand: userId,
                                    junction: JunctionOperator.Or,
                                },
                                {
                                    field: 'measureVerificationUsers.tenantUserId',
                                    op: QueryOperator.Eq,
                                    comparand: userId,
                                    junction: JunctionOperator.Or,
                                },
                            ],
                        },
                        {
                            field: 'implementationState',
                            op: QueryOperator.In,
                            comparand: [ImplementationStateEnum.WaitingForVerification],
                        },
                    ];
                    const aggregation = { predicates, aggregation: AggregationEnum.Count, aggregationProperty: 'id' } as any;
                    const response = await ApiService.api.aggregationMeasure(aggregation);
                    let count = 0;
                    if (response && response.data && response.data.returnValue) {
                        const r = response.data.returnValue as any;
                        count = typeof r === 'number' ? r : typeof r === 'string' ? parseInt(r, 10) : 0;
                    }
                    return { type: 'Verification', count };
                })(),
                // Review
                (async () => {
                    const predicates = [
                        { field: 'requestReviewById', op: QueryOperator.Eq, comparand: userId },
                        { field: 'planItemState', op: QueryOperator.Eq, comparand: PlanItemStateEnum.WaitingForApproval },
                    ];
                    const aggregation = { predicates, aggregation: AggregationEnum.Count, aggregationProperty: 'id' } as any;
                    const response = await ApiService.api.aggregationPlanItem(aggregation);
                    let count = 0;
                    if (response && response.data && response.data.returnValue) {
                        const r = response.data.returnValue as any;
                        count = typeof r === 'number' ? r : typeof r === 'string' ? parseInt(r, 10) : 0;
                    }
                    return { type: 'Review', count };
                })(),
            ]);

            this.activeAgendaCounts = {};
            results.forEach(({ type, count }) => (this.activeAgendaCounts[type] = count));
            this.canDeleteUser = results.every((r) => r.count === 0);
        } catch (e) {
            // Pokud dojde k chybě, raději smaž blokaci a povol smazání
            this.activeAgendaCounts = {};
            this.canDeleteUser = true;
        }
    }

    openAgendaTransfer() {
        const tenantIdParam = this.tenantId || (this.$route && this.$route.params && (this.$route.params as any).tenantId);
        this.$router.push({ name: 'AgendaTransfer', params: { tenantId: tenantIdParam } });
        this.deleteDialog.close();
    }

    getAllowedCategories(userId: string) {
        const categories: any[] = [];
        this.categories.forEach((c: CategoryModel) => {
            const result: any[] = [];
            result.push(
                c.categoryUsers
                    ?.filter((x: CategoryUserModel) => {
                        x.tenantUserId == userId;
                    })
                    .toArray()
            );
            // console.log(result);
        });
        return categories;
    }

    getLocationName(locationId?: string | null): string {
        if (!locationId) return '';
        const all: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        const found: LocationModel | undefined = (all as any).firstOrDefault
            ? (all as any).firstOrDefault((x: LocationModel) => x.id == locationId)
            : all.find((x) => x.id == locationId);
        return found?.name ?? '';
    }

    clearFilters() {
        this.filterName = '';
        this.filterDepartmentId = null;
        this.filterLocationId = null;
        this.filterCategoryId = null;
        this.filterRoleId = null;
        this.filterEmployeeId = '';
        this.filterPositionId = null;
    }

    getLocalStorageKey(): string {
        return `ams_accounts_nameDisplayFormat_${this.tenantId}`;
    }

    loadNameDisplayFormat() {
        const stored = window.localStorage.getItem(this.getLocalStorageKey());
        if (stored === 'lastFirst' || stored === 'firstLast') {
            this.nameDisplayFormat = stored;
        }
    }

    saveNameDisplayFormat() {
        window.localStorage.setItem(this.getLocalStorageKey(), this.nameDisplayFormat);
    }

    toggleNameDisplayFormat() {
        this.nameDisplayFormat = this.nameDisplayFormat === 'firstLast' ? 'lastFirst' : 'firstLast';
        this.saveNameDisplayFormat();
    }

    formatUserName(item: TenantUserModel): string {
        if (this.nameDisplayFormat === 'lastFirst') {
            return `${item.user?.lastName || ''} ${item.user?.firstName || ''}`.trim();
        } else {
            return `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim();
        }
    }

    get nameColumnHeader(): string {
        if (this.nameDisplayFormat === 'lastFirst') {
            return this.$i18n.t('settings.accounts.columnLastFirstName') as string;
        } else {
            return this.$i18n.t('settings.accounts.columnFirstLastName') as string;
        }
    }
}
